/**
 * Compliance Dashboard Page
 *
 * BIPA consent and profile photo adoption analytics including
 * consent funnel, photo sources, prompt fatigue, and orphaned faces.
 */

import { useState, useEffect } from 'react';
import { thirdPartyAnalyticsAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import PageHeader from '../components/analytics/PageHeader';
import MetricCard from '../components/analytics/MetricCard';
import DataTable from '../components/founder-dashboard/DataTable';
import DistributionChart from '../components/founder-dashboard/DistributionChart';
import {
  ShieldCheckIcon,
  PhotoIcon,
  FaceSmileIcon,
  ExclamationCircleIcon,
  ChartBarIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export default function CompliancePage() {
  const { currentEnv } = useEnvironment();
  const [overview, setOverview] = useState(null);
  const [bipaByOrg, setBipaByOrg] = useState(null);
  const [profileAdoption, setProfileAdoption] = useState(null);
  const [correlation, setCorrelation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, bipaRes, profileRes, correlationRes] = await Promise.all([
        thirdPartyAnalyticsAPI.getComplianceOverview(),
        thirdPartyAnalyticsAPI.getComplianceBipaByOrg(),
        thirdPartyAnalyticsAPI.getComplianceProfileAdoption(),
        thirdPartyAnalyticsAPI.getComplianceCorrelation(),
      ]);
      setOverview(overviewRes.data);
      setBipaByOrg(bipaRes.data);
      setProfileAdoption(profileRes.data);
      setCorrelation(correlationRes.data);
    } catch (err) {
      console.error('[CompliancePage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentEnv]);

  // Status indicators
  const getBipaStatus = () => {
    const rate = overview?.bipaAcceptanceRate || 0;
    if (rate < 50) return 'critical';
    if (rate < 70) return 'warning';
    return 'healthy';
  };

  const getGapStatus = () => {
    const gap = overview?.indexedVsEnabledGap || 0;
    if (gap > 10) return 'critical';
    if (gap > 5) return 'warning';
    return 'healthy';
  };

  if (error) {
    return (
      <div className="p-6">
        <PageHeader title="Compliance Dashboard" />
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Error loading compliance data</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Photo source distribution
  const photoSourceDistribution = {};
  (profileAdoption?.sources || []).forEach((s) => {
    photoSourceDistribution[s.source] = s.percentage;
  });

  // BIPA by org table columns
  const bipaColumns = [
    { key: 'organization_name', header: 'Organization', sortable: true },
    {
      key: 'acceptance_rate',
      header: 'Acceptance Rate',
      sortable: true,
      render: (val) => {
        const rate = val || 0;
        const isLow = rate < 70;
        return (
          <span className={isLow ? 'text-yellow-600 font-medium' : 'text-green-600'}>
            {isLow && '\u26A0\uFE0F '}{rate.toFixed(1)}%
          </span>
        );
      },
    },
    { key: 'total_prompted', header: 'Prompted', sortable: true },
    { key: 'total_accepted', header: 'Accepted', sortable: true },
  ];

  // Funnel data
  const funnel = overview?.funnel || {};
  const funnelSteps = [
    { label: 'Prompted', value: funnel.prompted || 0, pct: 100 },
    { label: 'Consented', value: funnel.consented || 0, pct: funnel.consentedPct || 0 },
    { label: 'Photo Uploaded', value: funnel.photoUploaded || 0, pct: funnel.photoUploadedPct || 0 },
    { label: 'Face Indexed', value: funnel.faceIndexed || 0, pct: funnel.faceIndexedPct || 0 },
  ];

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <PageHeader
        title="Compliance & Profile Adoption"
        description="BIPA consent and face recognition adoption metrics"
        onRefresh={fetchData}
        loading={loading}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="BIPA Acceptance Rate"
          value={overview?.bipaAcceptanceRate}
          format="percent"
          icon={ShieldCheckIcon}
          status={getBipaStatus()}
          loading={loading}
        />
        <MetricCard
          title="Profile Photo Rate"
          value={overview?.profilePhotoRate}
          format="percent"
          icon={PhotoIcon}
          loading={loading}
        />
        <MetricCard
          title="Face Enabled Rate"
          value={overview?.faceEnabledRate}
          format="percent"
          icon={FaceSmileIcon}
          loading={loading}
        />
        <MetricCard
          title="Indexed vs Enabled Gap"
          value={overview?.indexedVsEnabledGap}
          format="percent"
          icon={ExclamationCircleIcon}
          status={getGapStatus()}
          loading={loading}
        />
      </div>

      {/* Consent Funnel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          Consent Funnel
        </h3>
        {loading ? (
          <div className="h-24 bg-gray-50 rounded-lg animate-pulse"></div>
        ) : (
          <div className="flex items-center justify-between">
            {funnelSteps.map((step, idx) => (
              <div key={step.label} className="flex items-center flex-1">
                <div className="flex-1 text-center">
                  <div className={`inline-flex flex-col items-center p-4 rounded-lg ${
                    idx === 0 ? 'bg-blue-50 border border-blue-100' :
                    step.pct >= 70 ? 'bg-green-50 border border-green-100' :
                    step.pct >= 50 ? 'bg-yellow-50 border border-yellow-100' :
                    'bg-red-50 border border-red-100'
                  }`}>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">
                      {step.label}
                    </span>
                    <span className="text-2xl font-bold mt-1">
                      {step.pct.toFixed(0)}%
                    </span>
                    <span className="text-sm text-gray-500">
                      ({step.value.toLocaleString()})
                    </span>
                  </div>
                </div>
                {idx < funnelSteps.length - 1 && (
                  <ArrowRightIcon className="w-6 h-6 text-gray-300 mx-2 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photo Source + BIPA by Org */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Photo Source Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Photo Source Breakdown
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 rounded-lg animate-pulse"></div>
          ) : (
            <DistributionChart
              data={photoSourceDistribution}
              height={200}
              showPercentage
            />
          )}
        </div>

        {/* BIPA by Organization */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            BIPA Acceptance by Organization
          </h3>
          <div className="max-h-64 overflow-y-auto">
            <DataTable
              data={bipaByOrg?.organizations || []}
              columns={bipaColumns}
              loading={loading}
              emptyMessage="No organization data available"
            />
          </div>
        </div>
      </div>

      {/* Prompt Fatigue Analysis */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Prompt Fatigue Analysis
        </h3>
        {loading ? (
          <div className="h-32 bg-gray-50 rounded-lg animate-pulse"></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Avg Dismiss Count</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {(overview?.avgDismissCount || 0).toFixed(1)}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Users at Max Dismisses (4)</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {(overview?.usersAtMaxDismisses || 0).toFixed(1)}%
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Consent Withdrawal Rate</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {(overview?.consentWithdrawalRate || 0).toFixed(2)}%
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Orphaned Faces Warning */}
      {(overview?.orphanedFaces || 0) > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600" />
            <div>
              <h4 className="text-lg font-semibold text-yellow-800">
                Orphaned Faces in AWS: {overview?.orphanedFaces}
              </h4>
              <p className="text-sm text-yellow-700 mt-1">
                These are faces indexed in Rekognition that don't have a matching
                enabled user in the database. Review and clean up to ensure compliance.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Correlation Analysis */}
      {correlation && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            BIPA & Engagement Correlation
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <p className="text-sm text-purple-700">BIPA Accepted Users</p>
              <p className="text-2xl font-bold text-purple-800 mt-1">
                {(correlation?.acceptedEngagement || 0).toFixed(1)}%
              </p>
              <p className="text-xs text-purple-600 mt-1">Average Engagement Score</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">BIPA Declined Users</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {(correlation?.declinedEngagement || 0).toFixed(1)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">Average Engagement Score</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
